import React, { useState, useEffect } from "react";
import { ChatCompletionRequest, ToolCall } from "../../model_gen/chat";
import RequestOverview from "./RequestOverview";
import { RequestsService } from "../../services/RequestsService";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { CopyIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

interface RequestViewModalProps {
  requestId?: string;
  applicationId?: string;
  applicationActionId?: string;
  isVisible: boolean;
  onClose: () => void;
  apiKey: string;
  request?: ChatCompletionRequest;
}

const RequestViewModal: React.FC<RequestViewModalProps> = ({
  requestId,
  applicationId,
  applicationActionId,
  isVisible,
  onClose,
  apiKey,
  request: initialRequest,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [request, setRequest] = useState<ChatCompletionRequest | null>(
    initialRequest || null
  );

  useEffect(() => {
    // If a request is directly provided, use it
    if (initialRequest) {
      setRequest(initialRequest);
      return;
    }

    // Otherwise fetch the request if needed
    if (isVisible && requestId && applicationId && applicationActionId) {
      setIsLoading(true);
      RequestsService.getRequest(
        applicationId,
        applicationActionId,
        requestId,
        setIsLoading,
        apiKey
      ).subscribe(
        (fetchedRequest: ChatCompletionRequest) => {
          setRequest(fetchedRequest);
          setIsLoading(false);
        },
        (error: any) => {
          console.error("Failed to fetch request:", error);
          toast.error("Failed to fetch request");
          setIsLoading(false);
        }
      );
    }
  }, [
    isVisible,
    requestId,
    applicationId,
    applicationActionId,
    apiKey,
    initialRequest,
  ]);

  const handleClose = () => {
    setRequest(null);
    onClose();
  };

  const getMessageContent = (message: any, index: number) => {
    // Check if this is the last message
    const isLastMessage = index === (request?.params.messages?.length || 0) - 1;
    
    if (isLastMessage) {
      // Check for annotation label
      if (request?.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      
      // Check for test response
      const testResponse = request?.metadata?._maitai_test_request_response as
        | string
        | undefined;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
    }
    
    return message.content;
  };

  const getMessageToolCalls = (message: any, index: number) => {
    const isLastMessage = index === (request?.params.messages?.length || 0) - 1;
    
    if (isLastMessage && message.role === "assistant") {
      // Check for dataset response tool calls
      if (request?.metadata?._maitai_dataset_request_response_tool_calls) {
        return request.metadata._maitai_dataset_request_response_tool_calls;
      }
      
      // Check for annotation label
      if (request?.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.tool_calls;
      }
    }
    
    return message.tool_calls;
  };

  const formatMessagesForCopy = () => {
    if (!request) return "";
    
    const messages = request.params?.messages || [];
    return messages.map((message, index) => {
      const content = getMessageContent(message, index);
      let result = `${message.role.toUpperCase()}: ${content || ""}`;
      
      const toolCalls = getMessageToolCalls(message, index);
      if (toolCalls && toolCalls.length > 0) {
        const toolCallsFormatted = toolCalls.map((tool: ToolCall) => {
          return `[TOOL: ${tool.function.name}, ARGS: ${tool.function.arguments}]`;
        }).join(" ");
        
        result += ` ${toolCallsFormatted}`;
      }
      
      return result;
    }).join("\n");
  };

  const handleCopyToClipboard = () => {
    const formattedText = formatMessagesForCopy();
    navigator.clipboard.writeText(formattedText)
      .then(() => {
        toast.success("Messages copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy messages");
      });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Message skeleton */}
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-16 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!request) {
      return (
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-gc-med text-foreground">
                Request not found
              </h3>
              <p className="text-muted-foreground">
                Unable to load the request data.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <RequestOverview
        request={request}
        isEditable={false}
        showDetails={false}
        expanded={true}
        annotationLabel={
          request.metadata?._maitai_annotation_label
            ? JSON.parse(request.metadata._maitai_annotation_label as string)
            : undefined
        }
      />
    );
  };

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-background text-foreground border-border">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-gc-med text-foreground">
              Request Details
            </DialogTitle>
            {request && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyToClipboard}
                className="gap-2"
              >
                <CopyIcon className="w-4 h-4" />
                Copy Messages
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestViewModal;
